import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Eye, Loader2, Pencil, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDateID, formatRupiah } from "../lib/format";
import {
  ConfirmDialog, EmptyState, PaginationBar, tdClass, thClass,
} from "../components/Common";
import { Switch } from "../components/ui/switch";
import TransactionModal from "../components/TransactionModal";

export default function KhususAdmin() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, tx: null });
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setShowMenu(Boolean(r.data.show_special_menu))).catch(() => {});
  }, []);

  const toggleMenu = async (v) => {
    setShowMenu(v);
    try {
      const { data: cur } = await api.get("/settings");
      await api.put("/settings", { ...cur, show_special_menu: v });
      window.dispatchEvent(new Event("settings:refresh"));
      toast.success(v ? "Menu Pengeluaran Khusus ditampilkan di sidebar" : "Menu Pengeluaran Khusus disembunyikan dari sidebar");
    } catch (e) {
      setShowMenu(!v);
      toast.error(errMsg(e));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        type: "expense", hidden: "only", page: String(page),
        limit: String(limit), sort: "date", order: "desc",
        q, date_from: dateFrom, date_to: dateTo,
      });
      const [list, sum] = await Promise.all([
        api.get(`/transactions?${params.toString()}`),
        api.get("/hidden/summary"),
      ]);
      setItems(list.data.items);
      setTotal(list.data.total);
      setPages(list.data.pages);
      setSummary(sum.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const doDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/transactions/${deleting.id}`);
      toast.success("Catatan tersembunyi dihapus");
      setDeleting(null);
      load();
      window.dispatchEvent(new Event("saldo:refresh"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="khusus-admin-page">
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 flex items-start gap-3">
        <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="text-base font-bold text-cyan-400">Pengeluaran Khusus — Buku Catatan Admin</h2>
          <p className="text-sm text-amber-300 mt-1">
            Pengeluaran yang dicatat di sini <span className="font-semibold">tidak muncul</span> di
            Dashboard, Laporan, Rekap Bulanan, Saldo Kas, maupun file Export. Halaman ini tidak
            tercantum di menu dan hanya dapat dibuka oleh pemegang hak akses khusus.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
          {showMenu ? <Eye className="w-4 h-4 text-amber-600" /> : <EyeOff className="w-4 h-4 text-amber-600" />}
          <div>
            <p className="text-xs font-semibold text-slate-200">Tampilkan di Sidebar</p>
            <p className="text-[10px] text-amber-300">Default: tersembunyi</p>
          </div>
          <Switch
            data-testid="switch-show-special-menu"
            checked={showMenu}
            onCheckedChange={toggleMenu}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5" data-testid="hidden-total-card">
          <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase">Total Pengeluaran Tersembunyi</p>
          <p className="font-num text-2xl font-bold text-amber-300 mt-2">
            {summary ? formatRupiah(summary.total_expense) : "…"}
          </p>
        </div>
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5" data-testid="hidden-count-card">
          <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase">Jumlah Catatan</p>
          <p className="font-num text-2xl font-bold text-slate-100 mt-2">{summary ? summary.count : "…"}</p>
        </div>
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-5 flex items-center justify-center">
          {hasPerm("create_special_expense") && (
          <button
            data-testid="btn-add-hidden-expense"
            onClick={() => setModal({ open: true, tx: null })}
            className="bg-amber-600 text-white hover:bg-amber-700 font-medium px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 text-sm shadow-sm"
          >
            <Plus className="w-4 h-4" /> Tambah Catatan Tersembunyi
          </button>
          )}
          {!hasPerm("create_special_expense") && (
            <p className="text-xs text-slate-400 text-center">Anda hanya memiliki akses lihat</p>
          )}
        </div>
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-2">
        <input
          data-testid="input-hidden-search"
          className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-600"
          placeholder="Cari uraian / no bukti…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
        />
        <input
          data-testid="input-hidden-date-from"
          type="date"
          className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
        />
        <span className="text-xs text-slate-400">s/d</span>
        <input
          data-testid="input-hidden-date-to"
          type="date"
          className="rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
        />
        {(q || dateFrom || dateTo) && (
          <button
            data-testid="btn-hidden-reset-filter"
            onClick={() => { setQ(""); setDateFrom(""); setDateTo(""); setPage(1); }}
            className="text-xs font-semibold text-slate-400 hover:text-slate-100 px-2 py-2"
          >
            Reset Filter
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>
      ) : total === 0 ? (
        <EmptyState
          title="Belum ada catatan tersembunyi"
          description="Gunakan tombol di atas, atau centang 'Sembunyikan dari Dashboard & Laporan' saat menambah pengeluaran / Quick Input."
        />
      ) : (
        <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="table-hidden-expenses" className="w-full border-collapse text-sm text-left">
              <thead>
                <tr>
                  <th className={thClass}>No</th>
                  <th className={thClass}>Tanggal</th>
                  <th className={thClass}>No Bukti</th>
                  <th className={thClass}>Uraian</th>
                  <th className={thClass}>Kategori</th>
                  <th className={thClass}>Penerima/Tujuan</th>
                  <th className={`${thClass} text-right`}>Nominal</th>
                  <th className={thClass}>Keterangan</th>
                  <th className={`${thClass} text-center`}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t, i) => (
                  <tr key={t.id} className="hover:bg-amber-500/10/40" data-testid={`row-hidden-${t.id}`}>
                    <td className={tdClass}>{(page - 1) * limit + i + 1}</td>
                    <td className={`${tdClass} whitespace-nowrap`}>{formatDateID(t.date)}</td>
                    <td className={`${tdClass} font-num text-xs`}>{t.transaction_number}</td>
                    <td className={`${tdClass} font-medium text-slate-100 max-w-[240px]`}>
                      <span className="line-clamp-1 flex items-center gap-1.5">
                        <EyeOff className="w-3.5 h-3.5 text-amber-500 shrink-0" /> {t.description}
                      </span>
                    </td>
                    <td className={tdClass}>
                      <span className="bg-slate-800 text-slate-200 rounded-full px-2.5 py-0.5 text-xs font-medium">{t.category}</span>
                    </td>
                    <td className={tdClass}>{t.party || "-"}</td>
                    <td className={`${tdClass} text-right font-num font-semibold text-amber-300 whitespace-nowrap`}>
                      {formatRupiah(t.amount)}
                    </td>
                    <td className={`${tdClass} max-w-[160px]`}><span className="line-clamp-1 text-slate-400">{t.notes || "-"}</span></td>
                    <td className={`${tdClass} text-center`}>
                      <div className="flex items-center justify-center gap-1">
                        {hasPerm("edit_special_expense") && (
                          <button data-testid={`btn-edit-hidden-${t.id}`} title="Edit" onClick={() => setModal({ open: true, tx: t })} className="p-1.5 rounded-md hover:bg-slate-800/40 text-slate-400 hover:text-blue-300"><Pencil className="w-4 h-4" /></button>
                        )}
                        {hasPerm("delete_special_expense") && (
                          <button data-testid={`btn-delete-hidden-${t.id}`} title="Hapus" onClick={() => setDeleting(t)} className="p-1.5 rounded-md hover:bg-rose-500/15 text-slate-400 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBar page={page} pages={pages} total={total} onPage={setPage} limit={limit} onLimit={(n) => { setLimit(n); setPage(1); }} />
        </div>
      )}

      <TransactionModal
        open={modal.open}
        onOpenChange={(o) => setModal((m) => ({ ...m, open: o }))}
        type="expense"
        transaction={modal.tx}
        forceHidden={!modal.tx}
        onSaved={() => { load(); window.dispatchEvent(new Event("saldo:refresh")); }}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Hapus Catatan Tersembunyi?"
        description={deleting ? `Catatan "${deleting.description}" (${formatRupiah(deleting.amount)}) akan dihapus permanen.` : ""}
        onConfirm={doDelete}
        loading={deleteLoading}
      />
    </div>
  );
}
